import React, { useEffect, useState, createElement } from 'react';
import { ArrowLeftIcon, FileTextIcon, AlertTriangleIcon, CheckCircle2Icon, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
declare global {
  interface Window {
    Tally?: {
      openPopup: (formId: string, options?: Record<string, any>) => void;
      closePopup: (formId: string) => void;
    };
  }
}
export function InvestPage() {
  const [tallyReady, setTallyReady] = useState(false);
  useEffect(() => {
    // Check if Tally is already loaded
    if (window.Tally) {
      setTallyReady(true);
      return;
    }
    const loadTally = () => {
      const existing = document.querySelector('script[src="https://tally.so/widgets/embed.js"]');
      if (existing) {
        // Script tag exists but may not have loaded yet — poll for it
        const interval = setInterval(() => {
          if (window.Tally) {
            setTallyReady(true);
            clearInterval(interval);
          }
        }, 200);
        // Stop polling after 10 seconds
        setTimeout(() => clearInterval(interval), 10000);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://tally.so/widgets/embed.js';
      script.async = true;
      script.onload = () => {
        // Tally may take a moment to initialize after script loads
        const interval = setInterval(() => {
          if (window.Tally) {
            setTallyReady(true);
            clearInterval(interval);
          }
        }, 100);
        setTimeout(() => clearInterval(interval), 5000);
      };
      document.head.appendChild(script);
    };
    loadTally();
  }, []);
  const openInvestorForm = () => {
    if (window.Tally) {
      window.Tally.openPopup('44258r', {
        layout: 'modal',
        width: 700,
        autoClose: 5000
      });
    } else {
      // Fallback: open Tally form in new tab if widget didn't load
      window.open('https://tally.so/r/44258r', '_blank');
    }
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-[#111111]" data-id="element-2506">
      {/* Private Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50" data-id="element-2507">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-2508">
          <div className="flex items-center space-x-4" data-id="element-2509">
            <Link to="/investor-deck" className="text-gray-500 hover:text-[#631bb7] transition-colors" data-id="element-2510">
              <ArrowLeftIcon size={20} data-id="element-2511" />
            </Link>
            <div className="h-6 w-px bg-gray-200" data-id="element-2512"></div>
            <img src="https://cdn.magicpatterns.com/uploads/u2RsmpFkExbV8oAVAJrtqh/Caveo-Lockup-FC.png" alt="Caveo" className="h-6" data-id="element-2513" />
            <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-1 rounded-full uppercase tracking-wide" data-id="element-2514">
              Investor Portal
            </span>
          </div>
          <div className="text-sm text-gray-500" data-id="element-2515">
            Confidential & Proprietary
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-10" data-id="element-2516">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto space-y-4" data-id="element-2517">
          <h1 className="text-4xl font-bold text-[#111111]" data-id="element-2518">
            Ready to Invest in Caveo
          </h1>
          <p className="text-xl text-gray-500" data-id="element-2519">
            Here's what the process looks like:
          </p>
        </div>

        {/* Process Steps */}
        <div className="max-w-2xl mx-auto" data-id="element-2520">
          <div className="space-y-0" data-id="element-2521">
            <div className="flex gap-4" data-id="element-2522">
              <div className="flex flex-col items-center" data-id="element-2523">
                <div className="w-8 h-8 rounded-full bg-[#631bb7] text-white flex items-center justify-center font-bold text-sm shrink-0" data-id="element-2524">
                  1
                </div>
                <div className="w-0.5 bg-gray-200 flex-1 my-1" data-id="element-2525"></div>
              </div>
              <div className="pb-6" data-id="element-2526">
                <p className="text-lg font-semibold text-[#111111]" data-id="element-2527">
                  Please review the disclosures below.
                </p>
              </div>
            </div>
            <div className="flex gap-4" data-id="element-2528">
              <div className="flex flex-col items-center" data-id="element-2529">
                <div className="w-8 h-8 rounded-full bg-[#631bb7] text-white flex items-center justify-center font-bold text-sm shrink-0" data-id="element-2530">
                  2
                </div>
                <div className="w-0.5 bg-gray-200 flex-1 my-1" data-id="element-2531"></div>
              </div>
              <div className="pb-6" data-id="element-2532">
                <p className="text-lg font-semibold text-[#111111]" data-id="element-2533">
                  Please review the SAFE agreement.
                </p>
              </div>
            </div>
            <div className="flex gap-4" data-id="element-2534">
              <div className="flex flex-col items-center" data-id="element-2535">
                <div className="w-8 h-8 rounded-full bg-[#631bb7] text-white flex items-center justify-center font-bold text-sm shrink-0" data-id="element-2536">
                  3
                </div>
              </div>
              <div data-id="element-2537">
                <p className="text-lg font-semibold text-[#111111]" data-id="element-2538">
                  Once you're ready, click the button at the bottom of this
                  page.
                </p>
                <p className="text-gray-500 mt-1" data-id="element-2539">
                  This will take you to our investor interest form where we will
                  gather the necessary information to then send you a contract.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Important Disclosures */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden max-w-4xl mx-auto" data-id="element-2540">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-amber-50" data-id="element-2541">
            <AlertTriangleIcon size={20} className="text-amber-600" data-id="element-2542" />
            <h2 className="text-lg font-bold text-[#111111]" data-id="element-2543">
              Important Disclosures
            </h2>
          </div>
          <div className="px-6 py-6 space-y-4 text-gray-600 leading-relaxed text-sm" data-id="element-2544">
            <p data-id="element-2545">
              This form is for informational and interest-gathering purposes
              only. It does not constitute an offer to sell or a solicitation of
              an offer to buy any securities. Any such offer or solicitation
              will be made only by means of definitive offering documents and
              only in jurisdictions where permitted by law.
            </p>
            <p data-id="element-2546">
              Investments in AppEmbrace, Inc. (d/b/a Caveo) are available only
              to accredited investors as defined under Rule 501 of Regulation D
              of the Securities Act of 1933, as amended. An accredited investor
              generally includes individuals with an annual income exceeding
              $200,000 (or $300,000 jointly with a spouse) in each of the two
              most recent years, or a net worth exceeding $1,000,000, excluding
              the value of their primary residence.
            </p>
            <p data-id="element-2547">
              Investing in early-stage companies involves a high degree of risk.
              You may lose your entire investment. These securities are
              illiquid, meaning there is no guaranteed market for resale. Past
              performance of any company or investment is not indicative of
              future results.
            </p>
            <p data-id="element-2548">
              By completing this form, you are not making a binding commitment
              to invest, and AppEmbrace, Inc. is not obligating itself to accept
              your investment. All investments are subject to execution of
              definitive agreements and completion of AppEmbrace, Inc.'s
              investor review process.
            </p>
            <p className="font-medium text-gray-700" data-id="element-2549">
              This offering has not been reviewed or approved by the U.S.
              Securities and Exchange Commission or any state securities
              regulator.
            </p>
          </div>
        </div>

        {/* Embedded SAFE Agreement PDF */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden max-w-4xl mx-auto" data-id="element-2550">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50" data-id="element-2551">
            <FileTextIcon size={20} className="text-[#631bb7]" data-id="element-2552" />
            <h2 className="text-lg font-bold text-[#111111]" data-id="element-2553">SAFE Agreement</h2>
          </div>
          <div className="w-full" style={{
          height: '80vh'
        }} data-id="element-2554">
            <iframe src="https://drive.google.com/file/d/1w59cWXbjVzlR-My86Eke0EnrsG9-xluU/preview" className="w-full h-full border-0" allow="autoplay" title="Caveo SAFE Agreement" data-id="element-2555" />
          </div>
        </div>

        {/* CTA */}
        <div className="text-center max-w-4xl mx-auto py-6" data-id="element-2556">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 md:p-10" data-id="element-2557">
            <CheckCircle2Icon size={40} className="text-[#631bb7] mx-auto mb-4" data-id="element-2558" />
            <h2 className="text-2xl font-bold text-[#111111] mb-3" data-id="element-2559">
              Ready to proceed?
            </h2>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto" data-id="element-2560">
              After reviewing the disclosures and SAFE agreement above, click
              below to fill out our investor interest form. We'll gather the
              necessary information and send you a contract.
            </p>
            <button onClick={openInvestorForm} className="inline-flex items-center justify-center bg-[#631bb7] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#521099] transition-colors text-lg shadow-md cursor-pointer border-0" data-id="element-2561">
              Invest Now
              <ArrowRightIcon size={22} className="ml-3" data-id="element-2562" />
            </button>
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 py-8 mt-12" data-id="element-2563">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-gray-500" data-id="element-2564">
          <p data-id="element-2565">&copy; 2026 AppEmbrace, Inc. DBA Caveo. All rights reserved.</p>
          <p className="mt-2 text-xs text-gray-400" data-id="element-2566">
            This page contains confidential information intended only for
            authorized investors. Do not share or distribute without permission.
          </p>
        </div>
      </footer>
    </div>;
}